/**
 * Listings Service Layer
 * Optimized queries:
 * - listings (core data including description, spec_links)
 * - listing_ai_data (AI analysis fields)
 * - listing_images (individual image records)
 */

import { supabase } from '@/integrations/supabase/safeClient';

// Lightweight columns for list/card views (~60% smaller than full query)
export const LIGHT_COLUMNS = `
  id, title, price, top_category, listing_type, category, subcategory, item_category, condition,
  location, county, city, township, thumbnail_url,
  likes, views, chats, created_at,
  latitude, longitude, seller_id, is_negotiable,
  is_marked_complete, completed_via
`;

// Profile columns for seller info
export const PROFILE_COLUMNS = `
  id, name, county, city, township, avatar_url, verified, bio, username, trust_score
`;

// ============ Interfaces ============

export interface Profile {
  id: string;
  name: string;
  county: string;
  city?: string | null;
  township?: string | null;
  avatar_url?: string;
  verified: boolean;
  full_address?: string;
  bio?: string;
  username?: string;
  trust_score?: number;
}

export interface VehicleDetails {
  make?: string;
  model?: string;
  year?: number;
  mileage?: number;
  fuel_type?: string;
  transmission?: string;
  color?: string;
  vin?: string;
  accident_history?: boolean;
  num_owners?: number;
  inspection_date?: string;
  engine_size?: string;
  drive_type?: string;
}

export interface SubleaseDetails {
  monthly_rent?: number;
  deposit?: number;
  available_from?: string;
  available_until?: string;
  lease_term_months?: number;
  bedrooms?: number;
  bathrooms?: number;
  area_sqft?: number;
  furnished?: boolean;
  utilities_included?: boolean;
  pets_allowed?: boolean;
  parking_included?: boolean;
  sublease_type?: string;
}

// Lightweight listing for list/card views
export interface ListingLight {
  id: string;
  title: string;
  price: number;
  top_category?: string; // 'marketplace' | 'services'
  listing_type?: string; // 'used_goods' | 'used_cars' | 'sublease' | 'service'
  category: string;
  subcategory?: string;
  item_category?: string;
  condition?: string;
  location: string;
  county: string;
  city?: string;
  township?: string;
  thumbnail_url?: string;
  images?: string[]; // Optional - may come from demo data
  likes: number;
  views: number;
  chats?: number;
  created_at: string;
  content_updated_at?: string;
  latitude?: number;
  longitude?: number;
  seller_id: string;
  seller: Profile;
  description?: string; // Optional - may come from demo data
  is_negotiable?: boolean;
  is_marked_complete?: boolean;
  completed_via?: string;
}

export interface ListingImage {
  id: string;
  url: string;
  order_index: number;
  is_thumbnail: boolean;
}

export interface ListingAIData {
  ai_category_suggestion?: string;
  ai_confidence_score?: number;
  ai_price_evaluation?: string;
  duplicate_warning?: boolean;
  analyzed_at?: string;
}

// Full listing with all details
export interface ListingFull extends ListingLight {
  images: string[];
  description: string;
  location_type?: string;
  spec_links?: Record<string, string>;
  ai_data?: ListingAIData;
  vehicle_details?: VehicleDetails | null;
  sublease_details?: SubleaseDetails | null;
}

// ============ Query Functions ============

/**
 * Fetch listings for list/card views (lightweight query)
 * Returns only essential columns + seller profile
 * Uses status for proper filtering
 */
export async function fetchListingsLight(options?: {
  county?: string;
  category?: string;
  limit?: number;
  offset?: number;
  statusFilter?: ('active' | 'reserved')[];
}): Promise<{ data: ListingLight[]; error: any }> {
  // NOTE: We intentionally widen the Supabase query type here to avoid
  // TS2589 (excessively deep type instantiation) from complex select strings.
  let query: any = (supabase as any)
    .from('listings')
    .select(`
      ${LIGHT_COLUMNS},
      seller:profiles!seller_id(${PROFILE_COLUMNS})
    `)
    .is('deleted_at', null) // Soft delete filter
    .order('created_at', { ascending: false });

  // Filter by status - default to active and reserved
  const allowedStatuses = options?.statusFilter || ['active', 'reserved'];
  // Schema uses `status` (not `status_enum`).
  query = query.in('status', allowedStatuses);

  if (options?.county && options.county !== 'all') {
    query = query.eq('county', options.county);
  }

  if (options?.category) {
    query = query.eq('top_category', options.category);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 100) - 1);
  }

  const { data, error }: { data: any[] | null; error: any } = await query;

  if (error) {
    console.error('Error fetching listings:', error);
    return { data: [], error };
  }

  // Filter out listings without accessible seller profiles
  const validListings = (data || []).filter(
    (listing: any) => listing.seller !== null
  ) as ListingLight[];

  return { data: validListings, error: null };
}

/**
 * Fetch listings for a specific seller (lightweight query)
 */
export async function fetchListingsLightBySeller(sellerId: string): Promise<{
  data: ListingLight[];
  error: any;
}> {
  const { data, error } = await supabase
    .from('listings')
    .select(`
      ${LIGHT_COLUMNS},
      seller:profiles!seller_id(${PROFILE_COLUMNS})
    `)
    .eq('seller_id', sellerId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching seller listings:', error);
    return { data: [], error };
  }

  return { data: (data || []) as ListingLight[], error: null };
}

/**
 * Fetch full listing details for detail page
 * All fields are now on the listings table; images from listing_images
 */
export async function fetchListingDetails(listingId: string): Promise<{
  data: ListingFull | null;
  error: any;
}> {
  // Fetch listing with all columns + seller + detail tables
  const { data: listing, error: listingError } = await supabase
    .from('listings')
    .select(`
      *,
      seller:profiles!seller_id(${PROFILE_COLUMNS}),
      vehicle_details:listing_vehicle_details(*),
      sublease_details:listing_sublease_details(*)
    `)
    .eq('id', listingId)
    .maybeSingle();

  if (listingError || !listing) {
    return { data: null, error: listingError };
  }

  // Fetch images and AI data in parallel
  const [imagesResult, aiDataResult] = await Promise.all([
    supabase
      .from('listing_images')
      .select('id, url, order_index, is_thumbnail')
      .eq('listing_id', listingId)
      .order('order_index', { ascending: true }),
    supabase
      .from('listing_ai_data')
      .select('*')
      .eq('listing_id', listingId)
      .maybeSingle(),
  ]);

  const normalizedImages = imagesResult.data || [];
  const aiData = aiDataResult.data;
  let images = normalizedImages.map((img: ListingImage) => img.url);

  // Fallback: if listing_images is empty but thumbnail_url exists, use it
  if (images.length === 0 && (listing as any).thumbnail_url) {
    images = [(listing as any).thumbnail_url];
  }

  const fullListing: ListingFull = {
    ...listing,
    images,
    description: (listing as any).description || '',
    location_type: listing.location_type,
    spec_links: (listing as any).spec_links as Record<string, string> | undefined,
    ai_data: aiData || undefined,
    seller: listing.seller as Profile,
    vehicle_details: (listing as any).vehicle_details ?? null,
    sublease_details: (listing as any).sublease_details ?? null,
  };

  return { data: fullListing, error: null };
}

/**
 * Create a new listing via RPC (atomic: listing + vehicle/sublease details in one transaction)
 */
export async function createListing(data: {
  title: string;
  price: number;
  top_category: string; // 'marketplace' | 'services'
  listing_type: string; // 'used_goods' | 'used_cars' | 'sublease' | 'service'
  // Shadow writes for backward compat
  category?: string;
  subcategory?: string;
  item_category?: string;
  condition?: string;
  description: string;
  location: string;
  location_type: string;
  county: string;
  city?: string;
  township?: string;
  latitude?: number;
  longitude?: number;
  images: string[];
  seller_id: string;
  is_negotiable?: boolean;
  spec_links?: Array<{ title: string; url: string }>;
  imageHashes?: { url: string; hash: string }[];
  vehicle_details?: VehicleDetails;
  sublease_details?: SubleaseDetails;
}): Promise<{ data: { id: string } | null; error: any }> {
  const thumbnailUrl = data.images.length > 0 ? data.images[0] : null;

  const p_listing = {
    title: data.title,
    price: data.price,
    top_category: data.top_category,
    listing_type: data.listing_type,
    category: data.category ?? data.top_category,
    subcategory: data.subcategory ?? data.listing_type,
    item_category: data.item_category,
    condition: data.condition,
    description: data.description,
    location: data.location,
    location_type: data.location_type,
    county: data.county,
    city: data.city,
    township: data.township,
    latitude: data.latitude,
    longitude: data.longitude,
    thumbnail_url: thumbnailUrl,
    seller_id: data.seller_id,
    spec_links: data.spec_links && data.spec_links.length > 0 ? data.spec_links : null,
    is_negotiable: data.is_negotiable ?? false,
    status: 'active',
  };

  const imagePayload = data.images.map((url, index) => ({
    url,
    order_index: index,
    is_thumbnail: index === 0,
  }));

  const { data: rpcResult, error: rpcError } = await (supabase as any).rpc(
    'create_listing_with_details',
    {
      p_listing,
      p_vehicle: data.vehicle_details ?? null,
      p_sublease: data.sublease_details ?? null,
      p_images: imagePayload,
    }
  );

  if (rpcError || !rpcResult) {
    console.error('Error creating listing via RPC:', rpcError);
    return { data: null, error: rpcError };
  }

  const listingId =
    typeof rpcResult === 'string'
      ? rpcResult
      : typeof rpcResult === 'object' && rpcResult !== null && 'id' in rpcResult
        ? String((rpcResult as { id: string }).id)
        : null;

  if (!listingId) {
    console.error('Invalid create_listing_with_details response:', rpcResult);
    return { data: null, error: new Error('Failed to parse created listing id') };
  }

  return { data: { id: listingId }, error: null };
}

// ============================================================
// Mark as Complete — Service Wrappers
// These call the SECURITY DEFINER RPCs defined in migration
// 20260320150000_add_listing_mark_complete.sql.
// All auth/permission checks happen server-side in Postgres.
// ============================================================

/**
 * Possible error codes returned by mark/unmark RPCs.
 */
export type MarkCompleteErrorCode =
  | 'not_authenticated'
  | 'listing_not_found'
  | 'listing_already_sold'
  | 'not_authorized'
  | 'transaction_mark_protected'
  | 'internal_error';

export interface MarkCompleteResult {
  success: boolean;
  error_code: MarkCompleteErrorCode | null;
  message: string;
  listing_id?: string;
  completed_via?: 'manual' | 'transaction';
  completed_at?: string;
  already_complete?: boolean;
  already_unmarked?: boolean;
}

/**
 * Mark a listing as manually complete.
 *
 * - Only the listing's seller or an admin may call this.
 * - Does NOT change listing.status to 'sold' (manual ≠ accounting sale).
 * - Returns a typed result; never throws — check `success` field.
 *
 * @param listingId  UUID of the listing to mark.
 * @param note       Optional seller note (visible in admin/seller dashboard).
 */
export async function markListingManualComplete(
  listingId: string,
  note?: string
): Promise<MarkCompleteResult> {
  const { data, error } = await (supabase as any).rpc(
    'mark_listing_manual_complete',
    { p_listing_id: listingId, p_note: note ?? null }
  );

  if (error) {
    console.error('[markListingManualComplete] RPC error:', error);
    return {
      success: false,
      error_code: 'internal_error',
      message: error.message ?? 'Unexpected error calling mark_listing_manual_complete.',
    };
  }

  return data as MarkCompleteResult;
}

/**
 * Remove a manual "Mark as Complete" flag from a listing.
 *
 * - Transaction-marked listings (completed_via='transaction') cannot be
 *   unmarked by regular users — only admins can override.
 * - Returns a typed result; never throws — check `success` field.
 *
 * @param listingId  UUID of the listing to unmark.
 */
export async function unmarkListingManualComplete(
  listingId: string
): Promise<MarkCompleteResult> {
  const { data, error } = await (supabase as any).rpc(
    'unmark_listing_manual_complete',
    { p_listing_id: listingId }
  );

  if (error) {
    console.error('[unmarkListingManualComplete] RPC error:', error);
    return {
      success: false,
      error_code: 'internal_error',
      message: error.message ?? 'Unexpected error calling unmark_listing_manual_complete.',
    };
  }

  return data as MarkCompleteResult;
}

/**
 * Fetch like counts for multiple listings in a single query
 */
export async function fetchLikeCounts(listingIds: string[]): Promise<Map<string, number>> {
  const likesMap = new Map<string, number>();
  
  if (listingIds.length === 0) return likesMap;

  // Use a single query with grouping
  const { data, error } = await supabase
    .from('likes')
    .select('target_id')
    .eq('target_type', 'listing')
    .is('deleted_at', null)
    .in('target_id', listingIds);

  if (error) {
    console.error('Error fetching like counts:', error);
    return likesMap;
  }

  // Count likes per listing
  for (const like of data || []) {
    const count = likesMap.get(like.target_id) || 0;
    likesMap.set(like.target_id, count + 1);
  }

  return likesMap;
}

/**
 * Batch fetch comment counts for multiple listings in a single query
 */
export async function fetchCommentCounts(listingIds: string[]): Promise<Map<string, number>> {
  const commentsMap = new Map<string, number>();

  if (listingIds.length === 0) return commentsMap;

  const { data, error } = await supabase
    .from('comments')
    .select('target_id')
    .eq('target_type', 'listing')
    .is('deleted_at', null)
    .in('target_id', listingIds);

  if (error) {
    console.error('Error fetching comment counts:', error);
    return commentsMap;
  }

  for (const comment of data || []) {
    const count = commentsMap.get(comment.target_id) || 0;
    commentsMap.set(comment.target_id, count + 1);
  }

  return commentsMap;
}
